import { hiddenModeRefusal } from "../validations";
import { readHidden, writeHidden } from "../visibility";
import type { CustomizationHandler } from "./types";

/**
 * The quantity selector of the product page's add-to-cart form: the stepper
 * with its input and −/+ buttons.
 *
 * ## The selector
 *
 * `[data-store="product-detail"] .js-quantity`, and both halves are
 * load-bearing.
 *
 * **Why `.js-quantity`.** In 18 of the 22 SDK-carrying themes the product's
 * quantity snippet renders the stepper through the shared `form-input.tpl`
 * with `js-quantity` on its `form-group`, which wraps the label (when it is
 * inside), the input and both buttons. It is the hook the themes' own JS
 * binds the −/+ clicks to, and none of them shows or hides it.
 *
 * **Why the `data-store="product-detail"` scope.** `.js-quantity` is also
 * emitted by the quick shop and the grid cards of 13 themes, by the home
 * page's "main product" block, and by brasilia's cart line items. The product
 * page root carries `data-store="product-detail"` in every theme but
 * new_linkedman, and the related products render after it closes. The
 * tempting `.js-product-detail` is wrong: rio's home "main product" carries it
 * too, and `#product_form` is included by the home block of 11 themes. The
 * home block is `data-store="home-product-main"`, so this scope leaves it out.
 *
 * ## Where it does not apply
 *
 * - style and zen render no quantity on the product page: `not_found` is the
 *   truth there.
 * - material puts no `js-*` hook on its wrapper, and new_linkedman has neither
 *   `.js-quantity` nor `data-store="product-detail"`: `not_found` too.
 *
 * ## What is left behind
 *
 * The stepper sits inside a column with no `js-*` hook, which stays: hiding
 * leaves its width beside the add-to-cart button, and the "N en stock" line
 * where the store shows stock. In bahia and idea the "Cantidad" label sits
 * outside `.js-quantity` and stays as well.
 *
 * ## Durability
 *
 * Only Core's kit script touches a quantity wrapper's `display`
 * (`.js-form-quantity-container`, an ancestor in brasilia and rio, inline and
 * without `!important`), which the `hidden` class outranks. Selecting a
 * variant does not re-render the stepper, so `hidden` needs no re-applying.
 * The value the shopper typed is still submitted with the form: hiding only
 * removes the control, so an app that hides it usually also drives the
 * quantity it adds (e.g. through `cart:before_update`).
 *
 * Only `hidden`: one stepper per page, so no resolver context either.
 *
 * @see ./README.md
 */
export const productDetailQuantity: CustomizationHandler<"product-detail-quantity"> =
	{
		host: "storefront",
		selector: '[data-store="product-detail"] .js-quantity',

		read(element) {
			return { hidden: readHidden(element) };
		},

		operation(element, options) {
			if (options.hidden !== undefined) writeHidden(element, options.hidden);
		},

		values(element) {
			return { hidden: readHidden(element) };
		},

		validate(_element, options) {
			if (options.hidden !== undefined) {
				const refusal = hiddenModeRefusal(options.hidden);
				if (refusal) return `refused "hidden": ${refusal}`;
			}

			return null;
		},
	};
